import React, { useEffect, useRef, useState } from 'react';
import './projets.css'; 

function Projets() {
  const listRef = useRef(null);
  const containerRef = useRef(null);
  const audioRef = useRef(null);

  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 900);
  const [activeMobileIndex, setActiveMobileIndex] = useState(() => (window.innerWidth <= 900 ? -1 : 0));

  const [loadedImages, setLoadedImages] = useState(() => Array(9).fill(false));

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= 900;
      setIsMobile(mobile);
      // Keep behavior consistent when crossing the breakpoint
      setActiveMobileIndex((current) => {
        if (mobile) return current; // keep current selection on mobile
        // back to desktop: ensure something is active for the grid behavior
        return 0;
      });
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);


  const markImageLoaded = (index) => {
    setLoadedImages((prev) => {
      if (prev[index]) return prev;
      const next = [...prev];
      next[index] = true;
      return next;
    });
  };

  useEffect(() => {
    if (window.innerWidth <= 900) return;

    const list = listRef.current;
    const items = list.querySelectorAll('li');

    const applyIndex = (index) => {
      const cols = new Array(items.length)
        .fill()
        .map((_, i) => {
          items[i].dataset.active = (index === i).toString();
          return index === i ? '10fr' : '1fr';
        })
        .join(' ');
      list.style.setProperty('grid-template-columns', cols);
    };

    const setIndex = (event) => {
      const closest = event.target.closest('li');
      if (closest) {
        const index = [...items].indexOf(closest);
        applyIndex(index);
      }
    };

    const resync = () => {
      const w = Math.max(...[...items].map((i) => i.offsetWidth));
      list.style.setProperty('--article-width', w);
    };

    list.addEventListener('focus', setIndex, true);
    list.addEventListener('click', setIndex);
    list.addEventListener('pointermove', setIndex);
    window.addEventListener('resize', resync);

    applyIndex(0);
    resync();

    return () => {
      list.removeEventListener('focus', setIndex, true);
      list.removeEventListener('click', setIndex);
      list.removeEventListener('pointermove', setIndex);
      window.removeEventListener('resize', resync);
    };
  }, []);

  const playSound = () => {
    // Desktop only
    if (isMobile) return;

    // Stop current sound before starting a new one
    const current = audioRef.current;
    if (current) {
      try {
        current.pause();
        current.currentTime = 0;
      } catch (e) {}
      audioRef.current = null;
    }

    const songs = [
      'OpusLyra-A.ogg',
      'OpusLyra-B.ogg',
      'OpusLyra-C.ogg',
      'OpusLyra-D.ogg',
      'OpusLyra-E.ogg',
      'OpusLyra-F.ogg',
    ];

    
    const randomSong = songs[Math.floor(Math.random() * songs.length)];
    const audio = new Audio(`https://www.opusmajor.io/audio/${randomSong}`);
    audio.volume = 0.65;
    audioRef.current = audio;

    const cleanup = () => {
      if (audioRef.current === audio) audioRef.current = null;
      audio.removeEventListener('ended', cleanup);
      audio.removeEventListener('pause', cleanup);
    };

    audio.addEventListener('ended', cleanup);
    audio.addEventListener('pause', cleanup);

    audio.play().catch((err) => {
      console.error('Playback error:', err);
      cleanup();
    });
  };

  useEffect(() => {
    return () => {
      const a = audioRef.current;
      if (a) {
        try {
          a.pause();
          a.currentTime = 0;
        } catch (e) {}
        audioRef.current = null;
      }
    };
  }, []);

  return (
    <div className="container" ref={containerRef}>
      <h1 className="title">Projets</h1>
      {(!isMobile || activeMobileIndex === -1) && (
        <p className="intro">
          Vous trouverez une sélection de mes projets ou je mêle UI/UX, performances et web immersif.
          <br />
          Sites vitrines, market place, intranet, WebXR, 3D temps réel, réalité augmentée et j'en passe.
          Bref un condensé de mes réalisations. 
        </p>
      )}
      <ul
        className={`item-grid ${isMobile ? 'isMobile' : ''}`}
        ref={listRef}
        // onMouseOver={!isMobile ? playSound : undefined}
      >
        {[...Array(9)].map((_, i) => (
          <li
            key={i}
            data-active={(isMobile ? activeMobileIndex === i : i === 0).toString()}
            onClick={(e) => {
              if (!isMobile) return;
              e.preventDefault();
              e.stopPropagation();
              setActiveMobileIndex((current) => (current === i ? -1 : i));
            }}
          >
            <article>
              <h3>{["AeroBay", "Catacombes", "DS N°8", "Cabinet d'Avocat", "Site 3D", "DevXR", "Cartes AR", "Realité Mixte", "Terrasse 3D"][i]}</h3>
              <p>
                {[
                    "Refonte complète du front-end d’AeroBay, une plateforme B2B dédiée à l’aviation. Repenser l’expérience utilisateur tout en intégrant de nouvelles fonctionnalités métiers (gestion d’abonnements, espace utilisateur, recherches intelligentes). Design modernisé, performance optimisée, ajout de nombreuses fonctionnalités métier. Un projet structurant en collaboration avec l'équipe back-end, dans un contexte professionnel exigeant pour un résultat performant et ergonomique.",
                    "Création d’une expérience immersive en WebXR pour explorer les catacombes interdites de Paris. Ce projet propose une plongée interactive dans l’histoire souterraine à travers une visite en 3D directement accessible depuis un navigateur. Sur mobile, les salles peuvent être visités en réalité augmentée, et avec un casque immersif comme l’Oculus Quest 2, l’utilisateur peut s’immerger entièrement dans cet environnement mystérieux et chargé d’histoire.", 
                    "Collaboration avec DS Automobiles pour le lancement du flagship de la marque, la DS N°8. J’ai conçu l’ensemble de la section dédiée à la présentation interactive du véhicule en 3D, avec une expérience en réalité augmentée accessible directement depuis un smartphone (compatible iOS et Android). Pour une mise en valeur immersive d’un design avant-gardiste et des innovations technologiques de la marque.",
                    "Développement d’un site vitrine pour une avocate spécialisée. L’objectif : transmettre professionnalisme et confiance avec un design sobre, élégant et responsive. Intégration d’animations douces et de composants pour faciliter la prise de rendez-vous. L’objectif : inspirer confiance et faciliter la prise de contact.", 
                    "Conception d’un site expérimental entièrement en 3D utilisant Three.js. Navigation fluide entre différentes scènes, jeux de lumières, textures PBR et interactions en temps réel. Ce projet met en valeur la capacité à combiner performance, design et innovation technologique dans un environnement web.", 
                    "DevXR est la structure que j’ai fondée pour regrouper mes projets en WebXR. Le site présente à la fois des démos techniques, des expérimentations immersives et des cas d’usage concrets, le tout accessible directement depuis un navigateur. L’objectif : montrer aux entreprises et au grand public tout le potentiel de la réalité virtuelle, augmentée et mixte sur le web, sans application à installer. Démos immersives, tests techniques, proof-of-concepts… Un terrain d’expérimentation pour montrer les capacités du XR directement dans le navigateur.",
                    "Développement d’un système de cartes augmentées interactives. En scannant une carte via un smartphone, une animation 3D se déclenche en réalité augmentée sur la carte. Idéal pour des projets de gamification, d’éducation ou de communication innovante. Projet mêlant design, tech et accessibilité pour des expériences enrichies.", 
                    "Ancien portfolio permettant de voir la scene a la fois en 3D mais aussi de se projeter en VR (avec un casque) pour profiter pleinement des décors. On peut voir les différents éléménts a la fois avec les joystick et les mains ( testé avec Oculus Quest 2 et 3).",
                    "Création d'un site pour simuler une terrasse en 3D", 
                ][i]}
              </p>

                <svg  xmlns="http://www.w3.org/2000/svg"  width="24"  height="24"  viewBox="0 0 24 24"  fill="none"  stroke="currentColor"  strokeWidth="1"  strokeLinecap="round"  strokeLinejoin="round"  className="icon icon-tabler icons-tabler-outline icon-tabler-device-vision-pro">
                    {[
                        <><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M14.5 6.5l3 -2.9a2.05 2.05 0 0 1 2.9 2.9l-2.9 3l2.5 7.5l-2.5 2.55l-3.5 -6.55l-3 3v3l-2 2l-1.5 -4.5l-4.5 -1.5l2 -2h3l3 -3l-6.5 -3.5l2.5 -2.5l7.5 2.5z" /></>, 
                        <><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 4c4.418 0 8 3.358 8 7.5c0 1.901 -.755 3.637 -2 4.96l0 2.54a1 1 0 0 1 -1 1h-10a1 1 0 0 1 -1 -1v-2.54c-1.245 -1.322 -2 -3.058 -2 -4.96c0 -4.142 3.582 -7.5 8 -7.5z" /><path d="M10 17v3" /><path d="M14 17v3" /><path d="M9 11m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" /><path d="M15 11m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" /></>, 
                        <><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M7 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" /><path d="M17 17m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0" /><path d="M5 17h-2v-6l2 -5h9l4 5h1a2 2 0 0 1 2 2v4h-2m-4 0h-6m-6 -6h15m-6 0v-5" /></>, 
                        <><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M7 20l10 0" /><path d="M6 6l6 -1l6 1" /><path d="M12 3l0 17" /><path d="M9 12l-3 -6l-3 6a3 3 0 0 0 6 0" /><path d="M21 12l-3 -6l-3 6a3 3 0 0 0 6 0" /></>, 
                        <><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M6 17.6l-2 -1.1v-2.5" /><path d="M4 10v-2.5l2 -1.1" /><path d="M10 4.1l2 -1.1l2 1.1" /><path d="M18 6.4l2 1.1v2.5" /><path d="M20 14v2.5l-2 1.12" /><path d="M14 19.9l-2 1.1l-2 -1.1" /><path d="M12 12l2 -1.1" /><path d="M18 8.6l2 -1.1" /><path d="M12 12l0 2.5" /><path d="M12 18.5l0 2.5" /><path d="M12 12l-2 -1.12" /><path d="M6 8.6l-2 -1.1" /></>, 
                        <><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M12 7c1.143 0 2.235 .035 3.275 .104c1.017 .068 1.95 .207 2.798 .42c.813 .203 1.52 .505 2.119 .909a3.903 3.903 0 0 1 1.328 1.531c.326 .657 .48 1.48 .48 2.466c0 1.006 -.189 1.91 -.574 2.707c-.375 .779 -.886 1.396 -1.537 1.848a3.696 3.696 0 0 1 -2.16 .66c-.509 0 -.97 -.068 -1.382 -.21a5.84 5.84 0 0 1 -1.17 -.548a18.45 18.45 0 0 1 -1.045 -.695a9.104 9.104 0 0 0 -1.001 -.63a2.376 2.376 0 0 0 -1.13 -.301c-.373 0 -.75 .097 -1.132 .3c-.316 .17 -.65 .38 -1 .63c-.322 .23 -.67 .462 -1.047 .695a5.78 5.78 0 0 1 -1.168 .548c-.413 .142 -.872 .21 -1.378 .21a3.706 3.706 0 0 1 -2.165 -.659c-.651 -.452 -1.162 -1.07 -1.537 -1.848c-.385 -.798 -.574 -1.7 -.574 -2.709c-.004 -.98 .15 -1.802 .477 -2.46a3.897 3.897 0 0 1 1.33 -1.531c.6 -.403 1.307 -.704 2.12 -.907a16.088 16.088 0 0 1 2.8 -.423c1.04 -.071 2.13 -.107 3.273 -.107z" /></>, 
                        <><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 8v-2a2 2 0 0 1 2 -2h2" /><path d="M4 16v2a2 2 0 0 0 2 2h2" /><path d="M16 4h2a2 2 0 0 1 2 2v2" /><path d="M16 20h2a2 2 0 0 0 2 -2v-2" /><path d="M12 12.5l4 -2.5" /><path d="M8 10l4 2.5v4.5l4 -2.5v-4.5l-4 -2.5z" /><path d="M8 10v4.5l4 2.5" /></>,
                        <><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M17 15.328c2.414 -.718 4 -1.94 4 -3.328c0 -2.21 -4.03 -4 -9 -4s-9 1.79 -9 4s4.03 4 9 4" /><path d="M9 13l3 3l-3 3" /></>,
                        <><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M3 3m0 1a1 1 0 0 1 1 -1h16a1 1 0 0 1 1 1v16a1 1 0 0 1 -1 1h-16a1 1 0 0 1 -1 -1zm6 -1v18m6 -18v18" /></>,
                    ][i]}
                </svg>

              <a className="link_projets" href={[
                'https://www.aero-bay.com/', 
                'https://catacombes.xyz', 
                'https://www.dsautomobiles.fr/modeles-ds/dsn8.html', 
                'https://devxr.fr/sophie_test/', 
                'https://paulmarechal.xyz/', 
                'https://devxr.fr/', 
                'https://catacombes.xyz/Cards/', 
                'https://paulmarechal.xyz/portfolio/old/',
                'https://devxr.fr/terrasse', 
              ][i]} target="_blank" rel="noopener noreferrer">
                <span>Visiter</span>
              </a>
              <img
                src={`https://paulmarechal.xyz/assets/images_projets/${i}.webp`}
                loading="lazy"
                alt="Slide Visual"
                onLoad={() => markImageLoaded(i)}
                style={{
                  opacity: loadedImages[i] ? 1 : 0,
                  transform: loadedImages[i] ? 'translateY(0px)' : 'translateY(8px)',
                  transition: 'opacity 700ms ease, transform 700ms ease',
                  willChange: 'opacity, transform',
                }}
              />
            </article>
          </li>
        ))}
      </ul>
      {/* {!isMobile && (
        <div onClick={playSound} className="click-to-play">
          Test (click to play random sound)
        </div>
      )} */}
    </div>
  );
}

export default Projets;

// Essayer avec ça pour les cards : https://codepen.io/Kan3an/pen/xbGewKN